import React from "react";
import { View, TouchableOpacity, Text, Platform } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import NewTestButton from "../shared/NewTestButton";
import useWorkspaceAuth from "@/components/teams/useWorkspaceAuth";
import { useIsMobileUX } from "@/components/hooks/useIsMobileUX";
import SupportButton from "@/components/support/SupportButton";
import { supportEnabled } from "@/components/support/config";
import WhatsNewButton from "@/components/whatsNew/WhatsNewButton";
import { useTeamAccess } from "@/components/teams/useWorkspace";
import moment from "moment";
import tw from "twrnc";
import { t } from "@/localization";

export const AccountTypePill = ({ credits, subActive, onPress, style }) => {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={[
        tw`flex-row items-center justify-center rounded-lg p-3`,
        {
          backgroundColor: subActive ? "#FFD700" : "#008080", // Gold for active subscription, Teal for Pay As You Go
          backgroundImage: subActive
            ? "linear-gradient(45deg, #FFD700, #FFA500)" // Gold gradient
            : "linear-gradient(45deg, #008080, #20B2AA)", // Teal gradient
        },
        style,
      ]}
    >
      {subActive ? (
        <Text style={tw`text-base text-white font-bold`}>{t("Unlimited")}</Text>
      ) : (
        <View style={tw`flex-row items-center`}>
          <Text style={tw`text-base text-white font-bold`}>
            {t("Pay As You Go")}
          </Text>
          {credits > 0 && (
            <View
              style={tw`bg-white rounded-full w-6 h-6 items-center justify-center mx-1.5`}
            >
              <Text style={tw`text-xs font-bold text-[#008080]`}>
                {credits}
              </Text>
            </View>
          )}
        </View>
      )}
    </TouchableOpacity>
  );
};

const DashboardHeader = ({ navigation }) => {
  // The account you work in: yours, or one shared with you (its credits only
  // reach its admins).
  const {
    credits,
    subscription_status,
    subscription_access_expiry_datetime,
    account_type,
  } = useWorkspaceAuth();

  const subActive =
    subscription_access_expiry_datetime > moment().utc().format();

  const isMobileUX = useIsMobileUX();
  // Teams: in someone else's account the title is theirs, and creating
  // events and tests follows your role there; buying stays with the holder.
  const team = useTeamAccess();
  const canCreate = team.can("createEvent");
  // Tests are yours (they stay in your own account); moderators only moderate.
  const canTest = !team.isShared || (!!team.role && team.role !== "moderator");

  const createEvent = () => {
    navigation.navigate("CreateEvent");
  };

  const goToBuyCredits = () => {
    navigation.navigate("Plans", { source: "profile" });
  };
  // The plan and balance: the holder's, and in a shared account its admins',
  // who spend the credits. Buying stays with the holder.
  const showAccountPill =
    account_type === "business" &&
    (!team.isShared || team.can("spendCredits"));

  return (
    <View style={tw`w-full bg-white px-5`}>
      <View
        style={tw`w-full flex-row justify-between items-center ${
          Platform.OS === "web" ? "mt-5" : "mt-2"
        }`}
      >
        <View style={tw`flex-1 flex-row items-center`}>
          <Text
            numberOfLines={team.isShared ? 1 : undefined}
            style={tw`flex-grow-0 ${
              team.isShared ? "flex-shrink" : "flex-shrink-0"
            } font-extrabold text-[#2E3F52] ${
              isMobileUX ? "text-2xl" : "text-3xl"
            } pr-1.5 ${isMobileUX ? "" : "pl-5"}`}
          >
            {team.isShared ? team.name : t("My Events")}
          </Text>
          {canCreate && (
            <TouchableOpacity activeOpacity={1} onPress={createEvent}>
              <Ionicons
                name="add-circle"
                size={30}
                color="#F84123"
                style={tw`ml-1.5`}
              />
            </TouchableOpacity>
          )}
        </View>

        {!isMobileUX && canTest && (
          <NewTestButton
            style={isMobileUX ? tw`h-12 ml-2.5 px-3` : tw`h-12 ml-2.5 px-5`}
          />
        )}

        {!isMobileUX && showAccountPill && (
          <AccountTypePill
            credits={credits}
            subActive={subActive}
            onPress={team.isShared ? undefined : goToBuyCredits}
            style={tw`ml-2.5`}
          />
        )}

        <View style={tw`flex-row items-center`}>
          {account_type === "business" && (
            <WhatsNewButton
              style={isMobileUX ? tw`py-1 pr-4` : tw`ml-2 px-3 py-2.5`}
            />
          )}

          {supportEnabled && (
            <SupportButton
              style={isMobileUX ? tw`py-1 pr-3` : tw`px-3 py-2.5`}
            />
          )}

          <TouchableOpacity
            activeOpacity={1}
            onPress={() => {
              navigation.navigate("Profile");
            }}
          >
            <Ionicons
              name="person-outline"
              size={24}
              color="#3f5b7a"
              style={isMobileUX ? tw`py-1` : tw`pl-2 pr-5 py-2.5`}
            />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

export default DashboardHeader;
